import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Field, inputCls } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { SECTIONS, LEVEL_LABELS } from "@/lib/permissions";
import { CheckCircle2, XCircle, Trash2, Clock, Shield, User as UserIcon, KeyRound, X } from "lucide-react";
import { toast } from "sonner";
import { formatDateTR } from "@/lib/format";

const STATUS = {
  pending: { label: "Bekliyor", cls: "bg-[#D4A373]/20 text-[#8A5A44]", icon: Clock },
  approved: { label: "Onaylı", cls: "bg-[#4A6741]/15 text-[#4A6741]", icon: CheckCircle2 },
  rejected: { label: "Reddedildi", cls: "bg-[#B24C4C]/15 text-[#B24C4C]", icon: XCircle },
};

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [permsUser, setPermsUser] = useState(null);
  const [permsDraft, setPermsDraft] = useState({});

  const load = useCallback(async () => {
    if (user?.role !== "admin") return;
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kullanıcılar yüklenemedi");
    }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  const approve = async (u) => {
    await api.post(`/admin/users/${u.id}/approve`);
    toast.success(`${u.email} onaylandı`);
    load();
  };
  const reject = async (u) => {
    if (!window.confirm(`${u.email} reddedilsin mi?`)) return;
    await api.post(`/admin/users/${u.id}/reject`);
    toast.success("Reddedildi");
    load();
  };
  const remove = async (u) => {
    if (!window.confirm(`${u.email} kullanıcısı SİLİNSİN mi? Bu işlem geri alınamaz.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  const openPerms = (u) => {
    setPermsUser(u);
    const draft = {};
    SECTIONS.forEach((s) => {
      draft[s.key] = (u.permissions && u.permissions[s.key]) || "none";
    });
    setPermsDraft(draft);
  };

  const savePerms = async () => {
    try {
      await api.put(`/admin/users/${permsUser.id}/permissions`, { permissions: permsDraft });
      toast.success("Yetkiler güncellendi");
      setPermsUser(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const setAllPerms = (level) => {
    const draft = {};
    SECTIONS.forEach((s) => { draft[s.key] = level; });
    setPermsDraft(draft);
  };

  const pending = users.filter((u) => u.status === "pending");
  const others = users.filter((u) => u.status !== "pending");

  return (
    <div className="p-6 md:p-10 lg:p-12 fade-in max-w-[1400px]">
      <PageHeader
        eyebrow="Yönetim"
        title="Kullanıcı Yönetimi"
        description="Kayıt olan kullanıcıları onaylayın veya reddedin."
      />

      {pending.length > 0 && (
        <div className="mb-8" data-testid="pending-section">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#8A5A44] text-white flex items-center justify-center">
              <Clock size={16} strokeWidth={1.75} />
            </div>
            <h2 className="font-heading text-2xl text-[#2D2C2A]">Onay Bekleyen ({pending.length})</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pending.map((u) => (
              <div key={u.id} className="bg-white border-2 border-[#D4A373]/40 rounded-2xl p-5" data-testid={`pending-user-${u.id}`}>
                <p className="font-heading text-lg text-[#2D2C2A]">{u.name}</p>
                <p className="text-sm text-[#5C5B57] mb-1">{u.email}</p>
                <p className="text-xs text-[#5C5B57] mb-4">{formatDateTR(u.created_at)}</p>
                <div className="flex gap-2">
                  <button onClick={() => approve(u)} data-testid={`approve-${u.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#4A6741] text-white hover:bg-[#3d5636] rounded-xl px-3 py-2 text-sm font-medium transition-colors">
                    <CheckCircle2 size={16} /> Onayla
                  </button>
                  <button onClick={() => reject(u)} data-testid={`reject-${u.id}`}
                    className="inline-flex items-center justify-center gap-1 border border-[#B24C4C] text-[#B24C4C] hover:bg-[#B24C4C]/10 rounded-xl px-3 py-2 text-sm font-medium">
                    <XCircle size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="font-heading text-2xl text-[#2D2C2A] mb-4">Tüm Kullanıcılar ({others.length})</h2>
        <div className="bg-white border border-[#E6E4DD] rounded-2xl overflow-hidden">
          <table className="w-full text-left" data-testid="users-table">
            <thead>
              <tr className="text-xs tracking-[0.1em] uppercase font-bold text-[#5C5B57] border-b border-[#E6E4DD]">
                <th className="px-6 py-4">Ad Soyad</th>
                <th className="px-6 py-4">E-posta</th>
                <th className="px-6 py-4">Rol</th>
                <th className="px-6 py-4">Durum</th>
                <th className="px-6 py-4">Kayıt Tarihi</th>
                <th className="px-6 py-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {others.length === 0 && <tr><td colSpan="6" className="px-6 py-12 text-center text-[#5C5B57] text-sm">Onaylı kullanıcı yok.</td></tr>}
              {others.map((u) => {
                const s = STATUS[u.status] || STATUS.approved;
                const Icon = s.icon;
                return (
                  <tr key={u.id} className="border-b border-[#E6E4DD]/60 hover:bg-[#F0EFEA]/40" data-testid={`user-row-${u.id}`}>
                    <td className="px-6 py-4 text-[#2D2C2A] font-medium">{u.name}</td>
                    <td className="px-6 py-4 text-[#5C5B57] text-sm">{u.email}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${u.role === "admin" ? "bg-[#8A5A44]/15 text-[#8A5A44]" : "bg-[#F0EFEA] text-[#5C5B57]"}`}>
                        {u.role === "admin" ? <Shield size={12} /> : <UserIcon size={12} />}
                        {u.role === "admin" ? "Admin" : "Kullanıcı"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${s.cls}`}>
                        <Icon size={12} /> {s.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[#5C5B57] text-sm">{formatDateTR(u.created_at)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex gap-1">
                        {u.status === "rejected" && (
                          <button onClick={() => approve(u)} data-testid={`reapprove-${u.id}`} className="p-2 rounded-lg hover:bg-[#4A6741]/10 text-[#4A6741]" title="Onayla">
                            <CheckCircle2 size={16} />
                          </button>
                        )}
                        {u.role !== "admin" && (
                          <button onClick={() => openPerms(u)} data-testid={`perms-${u.id}`} className="p-2 rounded-lg hover:bg-[#8A5A44]/10 text-[#8A5A44]" title="Yetkiler">
                            <KeyRound size={16} />
                          </button>
                        )}
                        {u.role !== "admin" && (
                          <button onClick={() => remove(u)} data-testid={`delete-user-${u.id}`} className="p-2 rounded-lg hover:bg-[#B24C4C]/10 text-[#B24C4C]" title="Sil">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {permsUser && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in" onClick={() => setPermsUser(null)}>
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="perms-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-2xl text-[#2D2C2A]">Yetkiler</h3>
              <button onClick={() => setPermsUser(null)} className="text-[#5C5B57]"><X size={20} /></button>
            </div>
            <p className="text-sm text-[#5C5B57] mb-6">{permsUser.name} ({permsUser.email})</p>
            <div className="flex gap-2 mb-4">
              <button type="button" onClick={() => setAllPerms("none")} data-testid="perms-all-none" className="text-xs px-3 py-1.5 rounded-lg border border-[#E6E4DD] hover:bg-[#F0EFEA]">Hepsini Kapat</button>
              <button type="button" onClick={() => setAllPerms("view")} data-testid="perms-all-view" className="text-xs px-3 py-1.5 rounded-lg border border-[#E6E4DD] hover:bg-[#F0EFEA]">Hepsi Görüntüle</button>
              <button type="button" onClick={() => setAllPerms("edit")} data-testid="perms-all-edit" className="text-xs px-3 py-1.5 rounded-lg border border-[#E6E4DD] hover:bg-[#F0EFEA]">Hepsi Tam Yetki</button>
            </div>
            <div className="space-y-3">
              {SECTIONS.map((s) => (
                <div key={s.key} className="flex items-center justify-between gap-4">
                  <span className="text-sm text-[#2D2C2A]">{s.label}</span>
                  <select
                    className={`${inputCls} w-48`}
                    data-testid={`perm-${s.key}`}
                    value={permsDraft[s.key] || "none"}
                    onChange={(e) => setPermsDraft({ ...permsDraft, [s.key]: e.target.value })}
                  >
                    {Object.entries(LEVEL_LABELS).map(([v, lbl]) => (
                      <option key={v} value={v}>{lbl}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="flex gap-3 pt-6">
              <button type="button" onClick={() => setPermsUser(null)} className="flex-1 border border-[#E6E4DD] rounded-xl px-6 py-3 hover:bg-[#F0EFEA]">İptal</button>
              <button type="button" onClick={savePerms} data-testid="perms-save" className="flex-1 bg-[#8A5A44] text-white hover:bg-[#714835] rounded-xl px-6 py-3 font-medium">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
